import { useContext, useState } from 'react'
import { IntakeContext } from 'context/IntakeContext'
import { useFormDebounce } from 'hooks/useFormDebounce'
import { useRPQScore } from 'layouts/authenticated/pages/dashboard/hooks/useRPQScore'

import Box from '@mui/material/Box'
import { Button, Grid, IconButton } from '@mui/material'

import { AnimatePresence, motion } from 'framer-motion'
import { ArrowBack, Close } from '@mui/icons-material'
import { RiskTolerance } from './RiskTolerance'
import { TopPriorities } from './TopPriorities'
import { InvestorProfile } from './InvestorProfile'
import { FinancialFamiliarity } from './FinancialFamiliarity'
import { CoinFlip } from './CoinFlip'
import { DynamicInsight } from './DynamicInsight'
import { HypotheticalInvestment } from './HypotheticalInvestment'
import { Calculating } from './Calculating'
import { RiskProfileAdjustment } from './RiskProfileAdjustment'

export default function RiskAssessment({ closeModal }) {
  const { intakeState, updateIntakeStateAtKey } = useContext(IntakeContext)
  const { updateIntakeStateAndSubmit } = useFormDebounce()
  const rpqScore = useRPQScore()

  const [page, setPage] = useState(0)
  const [direction, setDirection] = useState(1)
  const [isComplete, setIsComplete] = useState(false)

  const handleNext = () => {
    setDirection(1)
    setPage((prev) => Math.min(prev + 1, pages.length - 1))
  }

  const getNextPage = (currentFieldName, userAnswer) => {
    if (!userAnswer) {
      return 'hypothetical'
    }

    switch (currentFieldName) {
      case '200100':
        if (userAnswer === 'yes') {
          return '20001000'
        } else {
          return '300100'
        }

      case '300100':
        return '30001000'

      case '20001000':
        if (userAnswer === 'yes') {
          return 'hypothetical'
        } else {
          return '30001000'
        }

      case '30001000':
        return 'hypothetical'

      default:
        return 'hypothetical'
    }
  }

  const handleAdjustment = () => {
    const adjustmentIndex = pages.findIndex(page => page.key === 'risk-profile-adjustment')
    if (adjustmentIndex !== -1) {
      setPage(adjustmentIndex)
      setDirection(1)
    }
  }

  const handleAdjustmentComplete = () => {
    const resultsIndex = pages.findIndex(page => page.key === 'dynamic-insight')
    if (resultsIndex !== -1) {
      setPage(resultsIndex)
      setDirection(1)
    }
  }

  const handleCoinFlipNext = (fieldName) => {
    const userAnswer = intakeState?.riskAssessment?.coinFlip?.[fieldName]
    const nextQuestion = getNextPage(fieldName, userAnswer)

    let nextPageIndex

    if (nextQuestion === 'hypothetical') {
      nextPageIndex = pages.findIndex((page) => page.key === 'hypothetical')
    } else {
      nextPageIndex = pages.findIndex((page) => page.key === nextQuestion)
    }

    if (nextPageIndex !== -1) {
      setPage(nextPageIndex)
      setDirection(1)
    } else {
      setPage((prev) => Math.min(prev + 1, pages.length - 1))
      setDirection(1)
    }
  }

  const handleBack = () => {
    setDirection(-1)
    setPage((prev) => Math.max(prev - 1, 0))
  }

  const handleClose = () => {
    updateIntakeStateAtKey({
      riskAssessment: {
        rpqScore: intakeState?.riskAssessment?.rpqScore,
      },
    })
    closeModal()
  }

  const handleComplete = () => {
    setIsComplete(true)
    updateIntakeStateAtKey({
      riskAssessment: {
        ...intakeState.riskAssessment,
        isComplete: true,
      },
    })
  }

  const handleCalculatingComplete = () => {
    handleComplete()
    handleNext()
  }

  const handleCloseAndSubmit = () => {
    updateIntakeStateAndSubmit(() => {
      const newState = updateIntakeStateAtKey({
        riskAssessment: {
          ...intakeState.riskAssessment,
          rpqScore,
          isComplete: true,
        },
      })
      return newState
    })()
    closeModal()
  }

  const variants = {
    enter: (dir) => ({
      x: dir > 0 ? 300 : -300,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (dir) => ({
      x: dir > 0 ? -300 : 300,
      opacity: 0,
    }),
  }

  const pages = [
    <RiskTolerance onNext={handleNext} key="risk-tolerance" />,
    <TopPriorities onNext={handleNext} key="top-priorities" />,
    <InvestorProfile onNext={handleNext} key="investor-profile" />,
    <FinancialFamiliarity onNext={handleNext} key="financial-familiarity" />,
    <CoinFlip
      onNext={handleCoinFlipNext}
      key="200100"
      header="Would you flip a coin if heads meant you win $200, but tails meant you lose $100?"
      fieldName="200100"
    />,
    <CoinFlip
      onNext={handleCoinFlipNext}
      key="300100"
      header="Would you flip a coin if heads meant you win $300, but tails meant you lose $100?"
      fieldName="300100"
    />,
    <CoinFlip
      onNext={handleCoinFlipNext}
      key="20001000"
      header="What if heads meant you win $2,000, but tails meant you lose $1,000?"
      fieldName="20001000"
    />,
    <CoinFlip
      onNext={handleCoinFlipNext}
      key="30001000"
      header="What if heads meant you win $3,000, but tails meant you lose $1,000?"
      fieldName="30001000"
    />,
    <HypotheticalInvestment onNext={handleNext} key="hypothetical" />,
    <Calculating onNext={handleCalculatingComplete} key="calculating" />,
    <DynamicInsight 
      key="dynamic-insight" 
      onAdjustment={handleAdjustment}
    />,
    <RiskProfileAdjustment 
      key="risk-profile-adjustment"
      onNext={handleAdjustmentComplete}
      currentRiskLevel={intakeState?.riskAssessment?.manualRiskLevel || rpqScore?.weightedScore || 0}
    />,
  ]

  return (
    <>
      <Box
        sx={{
          justifyContent: 'space-between',
          position: 'relative',
          display: 'flex',
          top: { xs: -8, sm: -64 },
        }}
      >
        {page > 0 && (
          <IconButton
            onClick={handleBack}
            sx={{
              position: 'absolute',
              top: 0,
              left: { xs: -8, sm: -16 },
              color: '#fff',
              zIndex: 1,
              backgroundColor: '#000',
              '&:hover': {
                backgroundColor: '#000',
              },
            }}
          >
            <ArrowBack />
          </IconButton>
        )}

        <IconButton
          onClick={pages.length - 2 === page ? handleCloseAndSubmit : handleClose}
          sx={{
            position: 'absolute',
            top: 0,
            right: { xs: -8, sm: -16 },
            color: '#fff',
            zIndex: 1,
            backgroundColor: '#000',
            '&:hover': {
              backgroundColor: '#000',
            },
          }}
        >
          <Close />
        </IconButton>
      </Box>
      <Grid
        sx={{ textAlign: 'left', overflow: 'hidden' }}
        px={{ xs: 0, sm: 4 }}
        pt={4}
        pb={{ xs: 2, sm: 4 }}
      >
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={page}
            custom={direction}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.3 }}
            style={{ position: 'relative', width: '100%' }}
          >
            {pages[page]}
            <Box>
              {page < pages.length - 2 ? (
                <></>
              ) : page < pages.length - 1 && (
                <>
                  <Box display="flex" justifyContent="space-between" mt={3}>
                    <Button
                      sx={{ textTransform: 'none' }}
                      variant="parcoRounded"
                      color="outline"
                      onClick={handleAdjustment}
                    >
                      Make an adjustment
                    </Button>
                    <Button
                      sx={{ textTransform: 'none' }}
                      variant="parcoRounded"
                      onClick={handleCloseAndSubmit}
                    >
                      This feels like me!
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          </motion.div>
        </AnimatePresence>
      </Grid>
    </>
  )
}
